import { useState } from "react";
import DashboardLayout from "@/pages/dashboard/components/DashboardLayout";
import { invoices, paymentMethods as initialPaymentMethods } from "@/mocks/dashboard";
import type { PaymentMethod } from "@/mocks/dashboard";
import AddCardModal from "./components/AddCardModal";

const statusStyles: Record<string, string> = {
  paid: "bg-primary-100 text-primary-800",
  pending: "bg-secondary-100 text-secondary-900",
  refunded: "bg-background-200 text-foreground-700",
};

export default function DashboardBillingPage() {
  const [filter, setFilter] = useState<"all" | "paid" | "pending" | "refunded">("all");
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(initialPaymentMethods);
  const [addCardOpen, setAddCardOpen] = useState(false);

  const handleAddCard = (card: PaymentMethod) => {
    setPaymentMethods((prev) => {
      const next = card.isDefault
        ? prev.map((p) => ({ ...p, isDefault: false }))
        : prev.slice();
      return [...next, card];
    });
  };

  const visible = invoices.filter((i) => (filter === "all" ? true : i.status === filter));

  const totalSpent = invoices
    .filter((i) => i.status === "paid")
    .reduce((acc, i) => acc + i.amount, 0);

  const yearlySaved = 248;

  return (
    <DashboardLayout title="Billing" subtitle="Cards, invoices, and what you've spent.">
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs text-foreground-600 uppercase tracking-wide">
                Total spent
              </p>
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-wallet-3-line"></i>
              </span>
            </div>
            <p className="font-heading text-3xl font-semibold text-foreground-950 mt-3">
              ${totalSpent.toLocaleString()}
            </p>
            <p className="text-xs text-foreground-600 mt-1">across {invoices.length} invoices</p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs text-foreground-600 uppercase tracking-wide">Saved this year</p>
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className="ri-discount-percent-line"></i>
              </span>
            </div>
            <p className="font-heading text-3xl font-semibold text-foreground-950 mt-3">
              ${yearlySaved}
            </p>
            <p className="text-xs text-foreground-600 mt-1">with bi-weekly recurring</p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs text-foreground-600 uppercase tracking-wide">Next charge</p>
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                <i className="ri-calendar-2-line"></i>
              </span>
            </div>
            <p className="font-heading text-3xl font-semibold text-foreground-950 mt-3">$174</p>
            <p className="text-xs text-foreground-600 mt-1">on Jun 19 · Visa · 4242</p>
          </div>
        </div>

        {/* Payment methods */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                Payment methods
              </h3>
              <p className="text-xs text-foreground-600 mt-0.5">
                Manage the cards we charge after each visit.
              </p>
            </div>
            <button
              onClick={() => setAddCardOpen(true)}
              className="inline-flex items-center justify-center gap-1.5 bg-foreground-950 hover:bg-foreground-900 text-background-50 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer self-start sm:self-auto"
            >
              <i className="ri-add-line"></i> Add card
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paymentMethods.map((p) => (
              <div
                key={p.id}
                className={`relative rounded-2xl p-5 text-background-50 bg-gradient-to-br ${p.color} min-h-[180px] flex flex-col justify-between`}
              >
                {p.isDefault && (
                  <span className="absolute top-4 right-4 inline-flex items-center bg-background-50/20 backdrop-blur px-2.5 py-1 rounded-full text-[10px] font-medium tracking-wide">
                    Default
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <i className="ri-bank-card-line text-2xl"></i>
                  <span className="font-medium tracking-wide">{p.brand}</span>
                </div>
                <div>
                  <p className="font-heading text-xl tracking-widest">•••• {p.last4}</p>
                  <div className="flex items-center justify-between mt-3 text-xs">
                    <div>
                      <p className="opacity-70">Cardholder</p>
                      <p className="font-medium mt-0.5">{p.holder}</p>
                    </div>
                    <div className="text-right">
                      <p className="opacity-70">Expires</p>
                      <p className="font-medium mt-0.5">{p.expiry}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={() => setAddCardOpen(true)}
              className="border-2 border-dashed border-background-300 rounded-2xl flex flex-col items-center justify-center min-h-[180px] text-foreground-600 hover:border-primary-400 hover:text-primary-700 hover:bg-background-100/50 transition-colors cursor-pointer"
            >
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-add-line text-xl"></i>
              </span>
              <p className="text-sm font-medium mt-2">Add new card</p>
            </button>
          </div>
        </div>

        {/* Invoices */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                Invoices
              </h3>
              <p className="text-xs text-foreground-600 mt-0.5">
                Every receipt, downloadable anytime.
              </p>
            </div>
            <div className="flex items-center bg-background-100 border border-background-200 rounded-full px-1 py-1 self-start md:self-auto overflow-x-auto max-w-full">
              {(["all", "paid", "pending", "refunded"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 sm:px-4 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap transition-colors cursor-pointer ${
                    filter === f
                      ? "bg-background-50 text-foreground-950 border border-background-200"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {visible.map((inv) => (
              <div
                key={inv.id}
                className="bg-background-100/60 border border-background-200 rounded-xl p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-950">{inv.number}</p>
                    <p className="text-xs text-foreground-600 mt-0.5">{inv.date}</p>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-medium capitalize whitespace-nowrap ${statusStyles[inv.status]}`}
                  >
                    {inv.status}
                  </span>
                </div>
                <p className="text-sm text-foreground-800 mt-2 truncate">{inv.service}</p>
                <p className="text-xs text-foreground-600 mt-1">{inv.method}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200">
                  <p className="font-semibold text-foreground-950">${inv.amount}</p>
                  <button
                    className="inline-flex items-center gap-1.5 bg-background-50 border border-background-200 text-foreground-800 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-download-line"></i> Download
                  </button>
                </div>
              </div>
            ))}
            {visible.length === 0 && (
              <div className="py-10 text-center text-sm text-foreground-600">
                No invoices match this filter.
              </div>
            )}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto -mx-6 px-6">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-xs text-foreground-600 border-b border-background-200">
                  <th className="font-medium py-3 pr-4">Invoice</th>
                  <th className="font-medium py-3 pr-4">Date</th>
                  <th className="font-medium py-3 pr-4">Service</th>
                  <th className="font-medium py-3 pr-4">Method</th>
                  <th className="font-medium py-3 pr-4">Status</th>
                  <th className="font-medium py-3 pr-4 text-right">Amount</th>
                  <th className="font-medium py-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-b border-background-200 last:border-b-0 hover:bg-background-100/50 transition-colors"
                  >
                    <td className="py-4 pr-4 font-medium text-foreground-950">{inv.number}</td>
                    <td className="py-4 pr-4 text-foreground-700 whitespace-nowrap">{inv.date}</td>
                    <td className="py-4 pr-4 text-foreground-700">{inv.service}</td>
                    <td className="py-4 pr-4 text-foreground-700 whitespace-nowrap">
                      {inv.method}
                    </td>
                    <td className="py-4 pr-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium capitalize whitespace-nowrap ${statusStyles[inv.status]}`}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td className="py-4 pr-4 font-medium text-foreground-950 text-right whitespace-nowrap">
                      ${inv.amount}
                    </td>
                    <td className="py-4 text-right">
                      <button
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 ml-auto"
                        aria-label="Download invoice"
                      >
                        <i className="ri-download-line"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {visible.length === 0 && (
              <div className="py-12 text-center text-sm text-foreground-600">
                No invoices match this filter.
              </div>
            )}
          </div>
        </div>

        {/* Billing address */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                Billing address
              </h3>
              <p className="text-sm text-foreground-700 mt-2 leading-relaxed">
                Eleanor Hayes<br />
                147 Linden Ave · Apt 3B<br />
                Brooklyn, NY 11215<br />
                United States
              </p>
            </div>
            <button className="inline-flex items-center gap-1.5 bg-background-100 hover:bg-background-200 text-foreground-800 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer self-start">
              <i className="ri-edit-line"></i> Edit
            </button>
          </div>
        </div>
      </div>

      <AddCardModal
        open={addCardOpen}
        onClose={() => setAddCardOpen(false)}
        onSubmit={handleAddCard}
      />
    </DashboardLayout>
  );
}